<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h2>POI登録</h2>
        <button @click="$emit('close')" class="close-btn">&times;</button>
      </div>

      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="name">名前 <span class="required">*</span></label>
            <input
              id="name"
              v-model="form.name"
              type="text"
              placeholder="東京タワー"
              required
              maxlength="255"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="latitude">緯度 <span class="required">*</span></label>
              <input
                id="latitude"
                v-model.number="form.latitude"
                type="number"
                step="0.000001"
                min="-90"
                max="90"
                placeholder="35.6586"
                required
              />
            </div>

            <div class="form-group">
              <label for="longitude">経度 <span class="required">*</span></label>
              <input
                id="longitude"
                v-model.number="form.longitude"
                type="number"
                step="0.000001"
                min="-180"
                max="180"
                placeholder="139.7454"
                required
              />
            </div>
          </div>

          <div class="form-group">
            <label for="address">住所</label>
            <input
              id="address"
              v-model="form.address"
              type="text"
              placeholder="東京都港区芝公園4-2-8"
              maxlength="500"
            />
          </div>

          <div class="form-group">
            <label for="category">カテゴリー</label>
            <input
              id="category"
              v-model="form.category"
              type="text"
              placeholder="観光スポット"
              maxlength="100"
            />
          </div>

          <div class="form-group">
            <label for="description">説明</label>
            <textarea
              id="description"
              v-model="form.description"
              rows="3"
              placeholder="POIの詳細説明"
              maxlength="10000"
            ></textarea>
          </div>

          <div class="form-group">
            <label for="phone">電話番号</label>
            <input
              id="phone"
              v-model="form.phone"
              type="tel"
              placeholder="03-1234-5678"
              maxlength="50"
            />
          </div>

          <div class="form-group">
            <label for="website">ウェブサイト</label>
            <input
              id="website"
              v-model="form.website"
              type="url"
              placeholder="https://example.com"
              maxlength="500"
            />
          </div>

          <div class="form-group">
            <label for="image_url">画像URL</label>
            <input
              id="image_url"
              v-model="form.image_url"
              type="url"
              placeholder="https://example.com/image.jpg"
              maxlength="500"
            />
          </div>

          <div class="form-group">
            <label for="tags">タグ（カンマ区切り）</label>
            <input
              id="tags"
              v-model="tagsInput"
              type="text"
              placeholder="観光, ランドマーク, 東京"
            />
          </div>

          <div v-if="error" class="error-message">
            {{ error }}
          </div>

          <div class="modal-footer">
            <button type="button" @click="$emit('close')" class="btn-cancel">
              キャンセル
            </button>
            <button type="submit" :disabled="loading" class="btn-submit">
              {{ loading ? '登録中...' : '登録' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { usePoiStore } from '../../stores/poi.js'

const props = defineProps({
  datasetId: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['close', 'created'])

const poiStore = usePoiStore()

const loading = ref(false)
const error = ref(null)

const form = ref({
  name: '',
  latitude: null,
  longitude: null,
  address: '',
  category: '',
  description: '',
  phone: '',
  website: '',
  image_url: '',
})

const tagsInput = ref('')

const handleSubmit = async () => {
  loading.value = true
  error.value = null

  try {
    // タグをパース
    const tags = tagsInput.value
      ? tagsInput.value.split(',').map(tag => tag.trim()).filter(tag => tag)
      : []

    const poiData = {
      dataset_id: props.datasetId,
      ...form.value,
      tags: tags.length > 0 ? tags : undefined,
    }

    // 未入力のフィールドを削除
    Object.keys(poiData).forEach(key => {
      if (poiData[key] === '' || poiData[key] === null || poiData[key] === undefined) {
        delete poiData[key]
      }
    })

    await poiStore.createPoi(poiData)

    emit('created')
    emit('close')
  } catch (err) {
    console.error('Failed to create POI:', err)
    error.value = err.response?.data?.error?.message || err.message || 'POIの登録に失敗しました'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #6c757d;
  line-height: 1;
  padding: 0;
}

.close-btn:hover {
  color: #000;
}

.modal-body {
  padding: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
}

.required {
  color: #dc3545;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.5rem 1rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 1rem;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #007bff;
}

.error-message {
  padding: 1rem;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  margin-bottom: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid #dee2e6;
}

.btn-cancel,
.btn-submit {
  padding: 0.5rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

.btn-cancel {
  background-color: #6c757d;
  color: white;
}

.btn-cancel:hover {
  background-color: #545b62;
}

.btn-submit {
  background-color: #007bff;
  color: white;
}

.btn-submit:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
